/* eslint-disable ts/ban-ts-comment */
import type { Option } from '@autobid/strapi-integration/typescript/webapi/Dictionary'
import type { SORTING_TYPES } from '@autobid/ui/constants/carsList/SORTING_TYPES'
import type { Ref } from 'vue'
import type { SearchCarsApiFilters } from '../car/data/useSearchCars'
import { useRouteQuery } from '@vueuse/router'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// #region types
export interface ItemListFiltersState {
  currentPage: number | undefined
  sortingType: (typeof SORTING_TYPES)[number] | undefined
  pageType: 'prev' | 'next' | undefined // used for animation and matomo purpose
  e174: number | undefined
  category: SearchCarsApiFilters['typeCategory'] | undefined
  manufacturerModel: SearchCarsApiFilters['manufacturerModel'] | undefined
  e17: number[] | undefined
  e223: number[] | undefined
  e19from: number | undefined
  e19to: number | undefined
  e68from: number | undefined
  e68to: number | undefined
  e21from: number | undefined
  e21to: number | undefined
  e31: number[] | undefined
  e70: number | undefined
  e24: number | undefined
  earliestDateOfAddition: SearchCarsApiFilters['earliestDateOfAddition'] | undefined
  stage: 'online' | 'all' | 'finished' | undefined
  supplierIds: number[] | undefined
}

export type ItemListFiltersStateWithRefs = {
  [K in keyof ItemListFiltersState]: Ref<ItemListFiltersState[K]>
}

export type ItemListFiltersStateKey = keyof ItemListFiltersState

export type FilterComposable = () => {
  filters: ItemListFiltersStateWithRefs
  areAllChecked: (
    possibleValues: Omit<Option, 'types'>[],
    currentValue: unknown[] | undefined
  ) => boolean
  toggleAll: (
    possibleValues: Omit<Option, 'types'>[],
    currentValue: unknown[] | undefined
  ) => string[]
  reset: () => void
}

// #endregion

// #region internal functions
function areAllChecked(possibleValues: Omit<Option, 'types'>[], currentValue: unknown[] | undefined) {
  if (!possibleValues.length || !currentValue?.length)
    return false

  const stringArray = (currentValue || []).map(el => String(el))

  return possibleValues.every(({ id }) => stringArray.includes(String(id)))
}

function toggleAll(possibleValues: Omit<Option, 'types'>[], currentValue: unknown[] | undefined): string[] {
  const allChecked = areAllChecked(possibleValues, currentValue)

  if (allChecked) {
    const ids = possibleValues.map(el => String(el.id))
    return (currentValue || [])
      .filter(el => !ids.includes(String(el)))
      .map(String)
  }

  return [...(currentValue || []), ...possibleValues.map(el => el.id)].map(
    String,
  )
}

function toNumber(param: unknown) {
  if (!param || String(param) === 'undefined')
    return

  const value = Array.isArray(param) ? Number(param[0]) : Number(param)

  return Number.isNaN(value) ? undefined : value
}

function toArray(param: string | string[] | null) {
  return (Array.isArray(param) ? param : [param]).filter(
    el => el !== null && el !== undefined,
  )
}

function toNumberArray(param: string | string[] | null) {
  return (Array.isArray(param) ? param : [param])
    .map(Number)
    .filter(el => !Number.isNaN(el))
}

function toString(param: unknown) {
  if (!param || String(param) === 'undefined')
    return

  if (Array.isArray(param))
    return String(param[0])

  return String(param)
}
// #endregion

export const useCarsListUrlFilter: FilterComposable = () => {
  const router = useRouter()
  const route = useRoute()
  const currentPageMode = ref<'push' | 'replace'>('push')

  const currentPage = useRouteQuery<
    ItemListFiltersState['currentPage'],
    ItemListFiltersState['currentPage']
  >('currentPage', 1, {
    // todo: delete me when the issue resolved: https://github.com/vueuse/vueuse/issues/4364 + remove all "@ts-ignore"
    // @ts-ignore
    router,
    route,
    transform: toNumber,
    mode: currentPageMode,
  })
  const sortingType = useRouteQuery<ItemListFiltersState['sortingType'], ItemListFiltersState['sortingType']>(
    'sortingType',
    undefined,
    {
      // @ts-ignore
      router,
      route,
      transform: el => toString(el) as ItemListFiltersState['sortingType'],
    },
  )
  const pageType = useRouteQuery<ItemListFiltersState['pageType'], ItemListFiltersState['pageType']>(
    'pageType',
    undefined,
    {
      // @ts-ignore
      router,
      route,
      transform: el => toString(el) as ItemListFiltersState['pageType'],
    },
  )
  const e174 = useRouteQuery<ItemListFiltersState['e174'], ItemListFiltersState['e174']>(
    'carList-e174',
    undefined,
    {
      // @ts-ignore
      router,
      route,
      transform: toNumber,
    },
  )
  const category = useRouteQuery<string[], string[]>(
    'carList-category',
    [],
    {
      // @ts-ignore
      router,
      route,
      transform: toArray,
    },
  )
  const manufacturerModel = useRouteQuery<
    string[],
    string[]
  >('carList-manufacturerModel', undefined, {
    // @ts-ignore
    router,
    route,
    transform: toArray,
  })

  const e17 = useRouteQuery<string[], ItemListFiltersState['e17']>(
    'carList-e17',
    [],
    {
      // @ts-ignore
      router,
      route,
      transform: toNumberArray,
    },
  )
  const e223 = useRouteQuery<string[], ItemListFiltersState['e223']>(
    'carList-e223',
    [],
    {
      // @ts-ignore
      router,
      route,
      transform: toNumberArray,
    },
  )
  const e19from = useRouteQuery<ItemListFiltersState['e19from'], ItemListFiltersState['e19from']>(
    'carList-e19from',
    undefined,
    {
      // @ts-ignore
      router,
      route,
      transform: toNumber,
    },
  )
  const e19to = useRouteQuery<ItemListFiltersState['e19to'], ItemListFiltersState['e19to']>(
    'carList-e19to',
    undefined,
    {
      // @ts-ignore
      router,
      route,
      transform: toNumber,
    },
  )
  const e68from = useRouteQuery<ItemListFiltersState['e68from'], ItemListFiltersState['e68from']>(
    'carList-e68from',
    undefined,
    {
      // @ts-ignore
      router,
      route,
      transform: toNumber,
    },
  )
  const e68to = useRouteQuery<ItemListFiltersState['e68to'], ItemListFiltersState['e68to']>(
    'carList-e68to',
    undefined,
    {
      // @ts-ignore
      router,
      route,
      transform: toNumber,
    },
  )
  const e21from = useRouteQuery<ItemListFiltersState['e21from'], ItemListFiltersState['e21from']>(
    'carList-e21from',
    undefined,
    {
      // @ts-ignore
      router,
      route,
      transform: toNumber,
    },
  )
  const e21to = useRouteQuery<ItemListFiltersState['e21to'], ItemListFiltersState['e21to']>(
    'carList-e21to',
    undefined,
    {
      // @ts-ignore
      router,
      route,
      transform: toNumber,
    },
  )
  const e31 = useRouteQuery<string[], ItemListFiltersState['e31']>(
    'carList-e31',
    [],
    {
      // @ts-ignore
      router,
      route,
      transform: toNumberArray,
    },
  )
  const e70 = useRouteQuery<ItemListFiltersState['e70'], ItemListFiltersState['e70']>(
    'carList-e70',
    undefined,
    {
      // @ts-ignore
      router,
      route,
      transform: toNumber,
    },
  )
  const e24 = useRouteQuery<ItemListFiltersState['e24'], ItemListFiltersState['e24']>(
    'carList-e24',
    undefined,
    {
      // @ts-ignore
      router,
      route,
      transform: toNumber,
    },
  )
  const earliestDateOfAddition = useRouteQuery<
    ItemListFiltersState['earliestDateOfAddition'],
    ItemListFiltersState['earliestDateOfAddition']
  >('carList-earliestDateOfAddition', undefined, {
    // @ts-ignore
    router,
    route,
    transform: el =>
      toString(el) as ItemListFiltersState['earliestDateOfAddition'],
  })
  const stage = useRouteQuery<ItemListFiltersState['stage'], ItemListFiltersState['stage']>(
    'carList-stage',
    undefined,
    {
      // @ts-ignore
      router,
      route,
      transform: el => toString(el) as ItemListFiltersState['stage'],
    },
  )
  const supplierIds = useRouteQuery<
    string[],
    ItemListFiltersState['supplierIds']
  >('carList-supplierIds', [], {
    // @ts-ignore
    router,
    route,
    transform: toNumberArray,
  })

  const reset = () => {
    currentPage.value = 1
    pageType.value = undefined
    e174.value = undefined
    category.value = []
    manufacturerModel.value = []
    e17.value = []
    e223.value = []
    e19from.value = undefined
    e19to.value = undefined
    e68from.value = undefined
    e68to.value = undefined
    e21from.value = undefined
    e21to.value = undefined
    e31.value = []
    e70.value = undefined
    e24.value = undefined
    earliestDateOfAddition.value = undefined
    stage.value = undefined
    supplierIds.value = []
  }

  /**
   * Wrap `refVar` which should reset the current page to 1 when it changes
   * and push the new value to the url
   */
  function createPaginationAwareFilterRef<T>(refVar: Ref<T>) {
    return computed({
      get: () => refVar.value,
      set: (val) => {
        refVar.value = val
        currentPageMode.value = 'replace'
        currentPage.value = 1
        currentPageMode.value = 'push'
      },
    })
  }

  return {
    filters: {
      currentPage,
      pageType,
      sortingType: createPaginationAwareFilterRef(sortingType),
      e174: createPaginationAwareFilterRef(e174),
      category: createPaginationAwareFilterRef(category),
      manufacturerModel: createPaginationAwareFilterRef(manufacturerModel),
      e17: createPaginationAwareFilterRef(e17),
      e223: createPaginationAwareFilterRef(e223),
      e19from: createPaginationAwareFilterRef(e19from),
      e19to: createPaginationAwareFilterRef(e19to),
      e68from: createPaginationAwareFilterRef(e68from),
      e68to: createPaginationAwareFilterRef(e68to),
      e21from: createPaginationAwareFilterRef(e21from),
      e21to: createPaginationAwareFilterRef(e21to),
      e31: createPaginationAwareFilterRef(e31),
      e70: createPaginationAwareFilterRef(e70),
      e24: createPaginationAwareFilterRef(e24),
      earliestDateOfAddition: createPaginationAwareFilterRef(earliestDateOfAddition),
      stage: createPaginationAwareFilterRef(stage),
      supplierIds: createPaginationAwareFilterRef(supplierIds),
    },
    areAllChecked,
    toggleAll,
    reset,
  }
}
